import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  IContent,
  IContentFindResponse,
  IContentItem,
  IQuickResponse,
  IVsmCommentResponse,
} from '../models/content.model';
import { IBaseTableResult } from '../models/table.model';
import { IContentGroup } from '../models/content-group.model';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { IListApiModel } from '../models/api.model';

@Injectable({
  providedIn: 'root',
})
export class ContentService {
  constructor(private http: HttpClient) {
  }

  getAllContentGroups(accountId: number): Observable<IContentGroup[]> {
    return this.http
      .get<IBaseTableResult<IContentGroup>>(
        environment.apiUrl + '/v1/contents',
        {
          params: {
            paginated: 'false',
            account_id: accountId.toString(),
          },
        },
      )
      .pipe(map((result: IBaseTableResult<IContentGroup>) => result.items));
  }

  getGroupContents(
    campaignId: number,
    contentGroupId: number,
  ): Observable<IContent[]> {
    return this.http.get<IContent[]>(
      environment.apiUrl + `/v1/campaigns/${campaignId}/contents`,
      {
        params: {
          content_group_id: contentGroupId.toString(),
        },
      },
    );
  }

  getContentInfo(contentId: number): Observable<IContent> {
    return this.http.get<IContent>(
      environment.apiUrl + `/v1/contents/${contentId}`,
    );
  }

  getCampaignInfo(contentId: number): Observable<IContent> {
    return this.http.get<IContent>(
      environment.apiUrl + `/v1/campaigns/${contentId}/contents`,
    );
  }

  updateContentGroupName(
    groupId: number,
    newGroupName: string,
  ): Observable<{ content_id: number }> {
    return this.http.put<{ content_id: number }>(
      environment.apiUrl + `/v1/contents/groups/${groupId}`,
      {
        group_name: newGroupName,
      },
    );
  }

  updateContent(
    contentId: number,
    newContentData: IContent,
  ): Observable<{ content_id: number }> {
    return this.http.put<{ content_id: number }>(
      environment.apiUrl + `/v1/contents/${contentId}`,
      {
        ...newContentData,
      },
    );
  }

  getUserReplay(type: string): Observable<{ reply_content: string }> {
    return this.http.get<{ reply_content: string }>(
      environment.apiUrl + `/v1/replies/${type}`,
    );
  }

  updateUserReplay(
    type: string,
    newReplay: string,
  ): Observable<{ reply_id: number }> {
    return this.http.put<{ reply_id: number }>(
      environment.apiUrl + `/v1/replies/${type}`,
      {
        reply_content: newReplay,
      },
    );
  }

  getQuickResponses(): Observable<{ items: IQuickResponse[] }> {
    return this.http.get<{ items: IQuickResponse[] }>(
      environment.apiUrl + '/v1/users/responses',
    );
  }

  getVsmComments(): Observable<IListApiModel<IVsmCommentResponse>> {
    return this.http.get<IListApiModel<IVsmCommentResponse>>(
      environment.apiUrl + '/v1/vsm/comments',
    );
  }

  getQuickResponse(id: number): Observable<IQuickResponse> {
    return this.http.get<IQuickResponse>(
      environment.apiUrl + `/v1/users/responses/${id}`,
    );
  }

  getVsmComment(id: number): Observable<IVsmCommentResponse> {
    return this.http.get<IVsmCommentResponse>(
      environment.apiUrl + `/v1/vsm/comments/${id}`,
    );
  }

  createQuickResponse(
    name: string,
    response: string,
  ): Observable<{ id: number }> {
    return this.http.post<{ id: number }>(
      environment.apiUrl + '/v1/users/responses',
      {
        name,
        response,
      },
    );
  }

  createVsmComment(name: string, response: string): Observable<{ id: number }> {
    return this.http.post<{ id: number }>(
      environment.apiUrl + '/v1/vsm/comments',
      {
        name,
        response,
      },
    );
  }

  updateQuickResponse(
    id: number,
    name: string,
    response: string,
  ): Observable<any> {
    return this.http.put<any>(
      environment.apiUrl + `/v1/users/responses/${id}`,
      {
        name,
        response,
      },
    );
  }

  updateVsmComment(
    id: number,
    name: string,
    response: string,
  ): Observable<{ id: number }> {
    return this.http.put<{ id: number }>(
      environment.apiUrl + `/v1/vsm/comments/${id}`,
      {
        name,
        response,
      },
    );
  }

  deleteVsmComment(id: number): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(
      environment.apiUrl + `/v1/vsm/comments/${id}`,
    );
  }

  findContentByCode(code: string, template?: string): Observable<IContentFindResponse> {
    const params: { [key: string]: string } = {
      search: code,
    };

    if (template) {
      params.template = template;
    }
    return this.http.get<IContentFindResponse>(
      environment.apiUrl + '/v1/contents/find',
      {
        params: new HttpParams({
          fromObject: params,
        }),
      },
    );
  }

  createContentGroup(grouName: string): Observable<{ content_id: number }> {
    return this.http.post<{ content_id: number }>(
      environment.apiUrl + '/v1/contents/groups',
      {
        group_name: grouName,
      },
    );
  }

  createAdminContentCode(
    data: IContent,
  ): Observable<{ data: { content_id: number } }> {
    return this.http.post<{ data: { content_id: number } }>(
      environment.apiUrl + '/v1/contents',
      {
        ...data,
      },
    );
  }

  getContentsByGroupId(groupId: number): Observable<IContent[]> {
    return this.http.get<IContent[]>(environment.apiUrl + `/v1/contents/find`, {
      params: {
        group_id: groupId.toString(),
      },
    });
  }

  requestContentHelp(contentGroupId: number, groupName: string): Observable<any> {
    return this.http.put(environment.apiUrl + `/v1/contents/groups/${contentGroupId}`, {
      request_content: true,
      group_name: groupName,
    });
  }

  getContentInfoByGroup(contentGroupId: number): Observable<IListApiModel<IContentItem>> {
    return this.http.get<IListApiModel<IContentItem>>(environment.apiUrl + `/v1/contents/groups/${contentGroupId}`);
  }
}
